import { useEffect } from "react";

// Keeps the device screen awake while the app is open (tour, video, browsing).
// Uses the Screen Wake Lock API; silently no-ops where unsupported.
// Re-acquires on tab visibility change (the OS drops the lock when hidden) and
// on the first user gesture (some browsers require a gesture before granting).
export function useWakeLock(enabled = true) {
  useEffect(() => {
    if (!enabled || typeof navigator === "undefined" || !("wakeLock" in navigator)) return;

    let lock = null;
    let cancelled = false;

    const acquire = async () => {
      if (cancelled || document.visibilityState !== "visible") return;
      try {
        lock = await navigator.wakeLock.request("screen");
        lock.addEventListener("release", () => { lock = null; });
      } catch (_) { /* denied / not allowed yet — will retry on gesture */ }
    };

    const onVisibility = () => { if (document.visibilityState === "visible" && !lock) acquire(); };
    const onGesture = () => { if (!lock) acquire(); };

    acquire();
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("click", onGesture);
    window.addEventListener("touchstart", onGesture);

    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("click", onGesture);
      window.removeEventListener("touchstart", onGesture);
      try { lock && lock.release(); } catch (_) {}
      lock = null;
    };
  }, [enabled]);
}
